import {
    Button,
    createListCollection,
    Field,
    Grid,
    GridItem,
    Image as ImageCh,
    Input,
    Portal,
    Select,
    Stack,
    Switch,
    Text,
} from "@chakra-ui/react";
import { React, useContext, useEffect, useRef, useState } from "react";

import FileButton from "@/components/files/fileButton";
import Map from "@/components/general/Map";
import MarkerFicha from "@/components/general/MarkerFicha";
import { googleMapsApiKey } from "@/config/config";
import { context } from "@/context/valuationContext";
import { useLeadingMaps } from "@/hooks/useBuscadorHook";

const Inmueble = ({ isGreaterThanMd }) => {
    //Elementos para utilizar autocompletado de google
    const refLocationSearch = useRef(null);
    const autocomplete = useRef(null);
    const [mapsObjectData] = useLeadingMaps(autocomplete, refLocationSearch);
    const [imageSelected, setImageSelected] = useState(null);
    const [idxSelected, setIdxSelected] = useState(null);
    const [imagesSV, setImagesSV] = useState([]);
    const [files, setFiles] = useState([]);
    const [filesCompressed, setFilesCompressed] = useState([]);
    const [isFromDevice, setIsFromDevice] = useState(false);

    //Elementos que se envian a marker y Map
    const [zoom, setZoom] = useState(10);
    const [marker, setMarker] = useState({
        position: { lat: 20.6739383, lng: -103.4054535 },
        title: "Guadalajara, Jalisco, México.",
    });

    //Elemento donde se guardara la informacion detallada cuando se mueva el marker o se ingrese una direccion desde input
    const [place, setPlace] = useState(null);

    const propertyTypeCollection = createListCollection({
        items: [
            { value: 3, label: "Casa habitación" },
            { value: 4, label: "Casa en condominio" },
            { value: 5, label: "Departamento" },
        ],
    });

    useEffect(() => {
        if (files.length > 0) {
            const file = files[0];
            const blobUrl = window.URL.createObjectURL(file);
            const img = new Image();
            img.src = blobUrl;
            img.onload = () => {
                window.URL.revokeObjectURL(blobUrl);
                const canvas = document.createElement("canvas");
                const newWidth = 400;
                const newHeight = 300;
                canvas.width = newWidth;
                canvas.height = newHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
                canvas.toBlob(
                    function (blob) {
                        const mimeType = "image/jpg";
                        const newFile = new File([blob], file.name, {
                            type: mimeType,
                            lastModified: Date.now(),
                        });
                        setFilesCompressed([newFile]);
                    },
                    file.type,
                    0.8
                );
            };
        }
    }, [files]);

    //Context
    const { updateInCondo, addDataProperty, step, updateStep } = useContext(context);

    useEffect(() => {
        if (mapsObjectData.lon || mapsObjectData.longitude) {
            const lat = mapsObjectData.lat || mapsObjectData.latitude;
            const lon = mapsObjectData.lon || mapsObjectData.longitude;

            // Actualiza la posición del marcador
            setMarker({
                position: { lat, lng: lon },
                title: mapsObjectData.location,
            });
            setZoom(14);

            // Utiliza Geocoder en lugar de fetch
            const geocoder = new window.google.maps.Geocoder();
            const latlng = { lat: parseFloat(lat), lng: parseFloat(lon) };

            geocoder.geocode({ location: latlng }, (results, status) => {
                if (status === "OK" && results[0]) {
                    setPlace(results[0]);
                } else {
                    console.error("Geocoding failed:", status);
                }
            });
        }
    }, [mapsObjectData]);

    //Elementos para guardar la info de elementos
    const [street, setStreet] = useState(null);
    const [externalNumber, setExternalNumber] = useState(null);
    const [internalNumber, setInternalNumber] = useState(null);
    const [lote, setLote] = useState(null);
    const [manzana, setManzana] = useState(null);
    const [suburb, setSuburb] = useState(null);
    const [postalCode, setPostalCode] = useState(null);
    const [state, setState] = useState(null);
    const [town, setTown] = useState(null);
    const [typeProperty, setTypeProperty] = useState(null);
    const [terrainSize, setTerrainSize] = useState(null);
    const [constructionSize, setConstructionSize] = useState(null);
    const [apartmentFloor, setApartmentFloor] = useState(0);
    const [elevator, setElevator] = useState(false);
    const [alert, setAlert] = useState(false);
    const [alertImage, setAlertImage] = useState(false);

    useEffect(() => {
        if (place) {
            if (place.address_components) {
                const address_comps = place.address_components;
                address_comps.forEach((element) => {
                    switch (element.types[0]) {
                        case "street_number": {
                            setExternalNumber(element.long_name);
                            break;
                        }
                        case "route": {
                            setStreet(element.long_name);
                            break;
                        }
                        case "political": {
                            setSuburb(element.long_name);
                            break;
                        }
                        case "locality": {
                            setTown(element.long_name);
                            break;
                        }
                        case "administrative_area_level_1": {
                            setState(element.long_name);
                            break;
                        }
                        case "postal_code": {
                            setPostalCode(element.long_name);
                            break;
                        }
                    }
                });
            }
        } else {
            setStreet(null);
            setExternalNumber(null);
            setSuburb(null);
            setPostalCode(null);
            setState(null);
            setTown(null);
        }
    }, [place]);

    useEffect(() => {
        if (street && externalNumber && state && town && postalCode && suburb && !place) {
            const address = `${street} ${externalNumber}, ${postalCode}, ${suburb}, ${town}, ${state}`;
            // Usamos Geocoder en lugar de fetch
            const geocoder = new window.google.maps.Geocoder();

            geocoder.geocode({ address: address }, (results, status) => {
                if (status === "OK" && results[0]) {
                    setPlace(results[0]);
                } else {
                    console.error("Geocoding failed:", status);
                }
            });
        }
    }, [street, externalNumber, state, town, postalCode, suburb, place]);

    const handleSubmit = () => {
        if (constructionSize > 19 && imageSelected) {
            setAlert(false);
            const data = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                state: state,
                municipality: town,
                colony: suburb,
                cp: Number(postalCode),
                street: street,
                batch: lote ? lote : "",
                block: manzana ? manzana : "",
                interior_number: internalNumber ? internalNumber : "",
                exterior_number: externalNumber,
                property_type: Number(typeProperty),
                land: terrainSize ? terrainSize : 0,
                construction: constructionSize,
                elevator: elevator === true ? 1 : 0,
                apartment_floor: apartmentFloor,
                front: imageSelected,
            };
            addDataProperty(data);
            updateStep(step + 1);
        } else {
            if (!imageSelected) {
                setAlertImage(true);
            }
            if (!constructionSize) {
                setAlert(true);
            }
        }
    };

    const handleGetStreetView = () => {
        if (place?.geometry?.location) {
            setImagesSV([]);

            // Se crea el servicio de Street View
            const streetViewService = new window.google.maps.StreetViewService();

            // Direccion de los angulos
            const locations = [{ heading: 40 }, { heading: 80 }, { heading: 140 }];

            // La función para obtener la imagen
            const getStreetViewImage = (location) => {
                return new Promise((resolve, reject) => {
                    streetViewService.getPanoramaByLocation(
                        place.geometry.location,
                        50,
                        (data, status) => {
                            if (status === "OK") {
                                const imageUrl = data.location.pano; // Aquí obtenemos la URL del panorama
                                resolve(
                                    `https://maps.googleapis.com/maps/api/streetview?size=600x400&pano=${imageUrl}&fov=100&heading=${location.heading}&pitch=0&key=${googleMapsApiKey}`
                                );
                            } else {
                                reject("Street view not found.");
                            }
                        }
                    );
                });
            };

            // Llamamos a las tres ubicaciones para obtener las imágenes
            const imagesPromises = locations.map(getStreetViewImage);

            Promise.all(imagesPromises)
                .then((imgs) => {
                    setImagesSV(imgs);
                })
                .catch((error) => {
                    console.error("Error al obtener imágenes de Street View:", error);
                });
        }
    };

    useEffect(() => {
        if (files.length > 0) {
            setIsFromDevice(true);
            handleTransformImage(filesCompressed[0]);
        } else {
            setIsFromDevice(false);
        }
    }, [filesCompressed]);

    const handleTransformImage = async (file) => {
        const img = await Promise.resolve(transformImage(file));
        setImageSelected(img);
    };

    async function transformImage(file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise((resolve, reject) => {
            reader.onload = () => {
                resolve(reader.result);
            };
            reader.onerror = (error) => {
                reject(error);
            };
        });
    }

    const handleSetImageFromGoogle = async (url) => {
        const blob = await fetch(url).then((res) => res.blob());
        const image = await Promise.resolve(transformImage(blob));
        setImageSelected(image);
    };

    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                <Grid
                    templateColumns={isGreaterThanMd ? "repeat(2, 1fr)" : "repeat(1, 1fr)"}
                    gap={6}
                    alignItems="baseline"
                    mt={2}
                >
                    <GridItem colSpan={1} textAlign="-webkit-left">
                        <Stack mb={4}>
                            <Input
                                id="ubicacion"
                                type="text"
                                ref={refLocationSearch}
                                rounded="full"
                                placeholder="Ingresa una ubicación"
                                fontSize="sm"
                                color="gray.600"
                                onChange={(e) => setPlace(e)}
                                px={4}
                            />
                        </Stack>
                        <Stack>
                            <Map
                                zoom={zoom}
                                style={{ height: "40vh", maxWidth: "80vw" }}
                                containerStyle={{ height: "40vh", maxWidth: "80vw" }}
                                center={marker.position}
                                onZoomChanged={setZoom}
                            >
                                <MarkerFicha marker={marker} draggable={true} setPlace={setPlace} />
                            </Map>
                        </Stack>
                    </GridItem>
                    <GridItem colSpan={1} textAlign="-webkit-left" alignSelf="self-start">
                        <Grid templateColumns="repeat(2, 1fr)" gap={6} alignItems="center" mt={0.5}>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Calle"
                                    type="text"
                                    rounded="full"
                                    placeholder="Calle*"
                                    fontSize="sm"
                                    color="gray.600"
                                    required
                                    value={street ? street : ""}
                                    //isDisabled={street && place?true:false}
                                    onChange={(e) => {
                                        setStreet(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Numero_exterior"
                                    type="number"
                                    rounded="full"
                                    placeholder="Numero exterior*"
                                    fontSize="sm"
                                    color="gray.600"
                                    required
                                    value={externalNumber ? externalNumber : ""}
                                    //isDisabled={externalNumber && place?true:false}
                                    onChange={(e) => {
                                        setExternalNumber(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Numero_interior"
                                    type="text"
                                    rounded="full"
                                    placeholder="Numero interior (si aplica)"
                                    fontSize="sm"
                                    color="gray.600"
                                    onChange={(e) => {
                                        setInternalNumber(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Lote"
                                    type="text"
                                    rounded="full"
                                    placeholder="Lote"
                                    fontSize="sm"
                                    color="gray.600"
                                    onChange={(e) => {
                                        setLote(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Manzana"
                                    type="text"
                                    rounded="full"
                                    placeholder="Manzana"
                                    fontSize="sm"
                                    color="gray.600"
                                    onChange={(e) => {
                                        setManzana(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Colonia"
                                    type="text"
                                    rounded="full"
                                    placeholder="Colonia*"
                                    fontSize="sm"
                                    color="gray.600"
                                    required
                                    value={suburb ? suburb : ""}
                                    //isDisabled={suburb && place?true:false}
                                    onChange={(e) => {
                                        setSuburb(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Codigo_postal"
                                    type="text"
                                    rounded="full"
                                    placeholder="Código postal*"
                                    fontSize="sm"
                                    color="gray.600"
                                    required
                                    value={postalCode ? postalCode : ""}
                                    disabled={postalCode && place ? true : false}
                                    onChange={(e) => {
                                        setPostalCode(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Estado"
                                    type="text"
                                    rounded="full"
                                    placeholder="Estado*"
                                    fontSize="sm"
                                    color="gray.600"
                                    required
                                    value={state ? state : ""}
                                    disabled={state && place ? true : false}
                                    onChange={(e) => {
                                        setState(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Municipio"
                                    type="text"
                                    rounded="full"
                                    placeholder="Municipio*"
                                    fontSize="sm"
                                    color="gray.600"
                                    required
                                    value={town ? town : ""}
                                    disabled={town && place ? true : false}
                                    onChange={(e) => {
                                        setTown(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Select.Root
                                    collection={propertyTypeCollection}
                                    id="tipo_inmueble"
                                    rounded="full"
                                    fontSize="sm"
                                    color="gray.600"
                                    required
                                    onValueChange={(details) => {
                                        setTypeProperty(details.value[0]);
                                        updateInCondo(details.value[0] != "3");
                                    }}
                                >
                                    <Select.HiddenSelect />
                                    <Select.Control>
                                        <Select.Trigger pl={4} rounded="full">
                                            <Select.ValueText placeholder="Tipo de inmueble*" />
                                        </Select.Trigger>
                                        <Select.IndicatorGroup pr={4}>
                                            <Select.Indicator />
                                        </Select.IndicatorGroup>
                                    </Select.Control>
                                    <Portal>
                                        <Select.Positioner>
                                            <Select.Content p={2}>
                                                {propertyTypeCollection.items.map((option) => (
                                                    <Select.Item
                                                        item={option}
                                                        key={option.value}
                                                        p={1}
                                                        color="gray.600"
                                                    >
                                                        {option.label}
                                                        <Select.ItemIndicator />
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Positioner>
                                    </Portal>
                                </Select.Root>
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Terreno"
                                    type="number"
                                    rounded="full"
                                    placeholder={typeProperty == "5" ? "Terreno" : "Terreno*"}
                                    fontSize="sm"
                                    color="gray.600"
                                    min={0}
                                    required={typeProperty != "5"}
                                    onChange={(e) => {
                                        setTerrainSize(e.target.value);
                                    }}
                                    px={4}
                                />
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Input
                                    id="Construccion"
                                    type="number"
                                    rounded="full"
                                    placeholder="Construcción*"
                                    fontSize="sm"
                                    color="gray.600"
                                    min={0}
                                    onChange={(e) => {
                                        setConstructionSize(e.target.value);
                                    }}
                                    required
                                    px={4}
                                />
                                {alert ? (
                                    <Text colSpan={1} style={{ color: "red" }}>
                                        Ingresa un numero valido
                                    </Text>
                                ) : null}
                            </GridItem>
                            {typeProperty == "5" ? (
                                <GridItem
                                    colSpan={isGreaterThanMd ? 1 : 2}
                                    textAlign="-webkit-left"
                                >
                                    <Input
                                        id="numero_piso"
                                        type="number"
                                        rounded="full"
                                        placeholder="Piso*"
                                        fontSize="sm"
                                        color="gray.600"
                                        min={0}
                                        onChange={(e) => {
                                            setApartmentFloor(e.target.value);
                                        }}
                                        required
                                        px={4}
                                    />
                                </GridItem>
                            ) : null}
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <Field.Root alignItems="start">
                                    <Switch.Root
                                        id="email-alerts"
                                        onCheckedChange={(details) => {
                                            setElevator(details.checked);
                                        }}
                                    >
                                        <Switch.Label> ¿Cuenta con elevador? </Switch.Label>
                                        <Switch.HiddenInput />
                                        <Switch.Control />
                                    </Switch.Root>
                                </Field.Root>
                            </GridItem>
                            <GridItem colSpan={2} textAlign="-webkit-left">
                                <Text fontSize="md" fontWeight="600" mb={4}>
                                    Imagen de la propiedad
                                </Text>
                                {alertImage ? (
                                    <Text colSpan={1} style={{ color: "red" }}>
                                        Selecciona una imagen
                                    </Text>
                                ) : null}
                                {imagesSV.length > 0 && !isFromDevice ? (
                                    <Grid
                                        templateColumns={{
                                            base: "repeat(1, 1fr)",
                                            md: "repeat(3, 1fr)",
                                        }}
                                        gap={6}
                                        mb={4}
                                    >
                                        {imagesSV?.map((image, index) => (
                                            <GridItem
                                                key={index}
                                                colSpan={1}
                                                _hover={{ cursor: "pointer", opacity: 0.8 }}
                                                borderWidth={index === idxSelected ? "4px" : "0px"}
                                                borderStyle="solid"
                                                borderColor={
                                                    index === idxSelected
                                                        ? "brand.500"
                                                        : "transparent"
                                                }
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setIdxSelected(index);
                                                    handleSetImageFromGoogle(image);
                                                    //setImageSelected(image)
                                                }}
                                            >
                                                <ImageCh src={image} alt="imagen" />
                                            </GridItem>
                                        ))}
                                    </Grid>
                                ) : null}
                                {imageSelected && (
                                    <Stack>
                                        <ImageCh
                                            src={imageSelected}
                                            alt="imagen"
                                            style={{ marginBottom: "10px" }}
                                        />
                                    </Stack>
                                )}
                            </GridItem>
                            <GridItem
                                colSpan={isGreaterThanMd ? 1 : 2}
                                textAlign="-webkit-left"
                                justifySelf="center"
                            >
                                <Button
                                    rounded="full"
                                    colorPalette="brand"
                                    fontSize="sm"
                                    px={4}
                                    disabled={
                                        place?.geometry?.location && !isFromDevice ? false : true
                                    }
                                    onClick={handleGetStreetView}
                                    mb="auto"
                                    bg="brand.500"
                                >
                                    Seleccionar de google
                                </Button>
                            </GridItem>
                            <GridItem colSpan={isGreaterThanMd ? 1 : 2} textAlign="-webkit-left">
                                <FileButton
                                    label="Seleccionar del dispositivo"
                                    value={files}
                                    setValue={setFiles}
                                />
                            </GridItem>

                            <GridItem colSpan={2} textAlign="-webkit-right" mt={6}>
                                <Button
                                    rounded="full"
                                    colorPalette="brand"
                                    bg="brand.500"
                                    color="black"
                                    border="0"
                                    px="16"
                                    _hover={{ bg: "brand.600" }}
                                    type="submit"
                                >
                                    Siguiente
                                </Button>
                            </GridItem>
                        </Grid>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default Inmueble;
